"use client";

import { Beaker, Droplets, Sun, Sprout } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
export default function MetodoOperativo() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: cardsRef,
    isVisible: cardsVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const metrics = [{
    value: "100%",
    label: "EFFICIENZA"
  }, {
    value: "24/7",
    label: "MONITORAGGIO"
  }, {
    value: "ISO",
    label: "STANDARD"
  }, {
    value: "100%",
    label: "TRACCIABILITÀ"
  }];
  const protocolSteps = [{
    number: "01",
    title: "SOPRALLUOGO TECNICO",
    description: "Analisi preliminare degli impianti aeraulici e dei sistemi di estrazione per mappare lo stato di conservazione e le criticità funzionali.",
    icon: Sprout,
    timing: "INIZIALE"
  }, {
    number: "02",
    title: "ISPEZIONE VIDEO",
    description: "Video-ispezione robotizzata ad alta risoluzione per documentare lo stato interno dei condotti e identificare depositi o anomalie.",
    icon: Beaker,
    timing: "ANALISI"
  }, {
    number: "03",
    title: "INTERVENTO SPECIALISTICO",
    description: "Bonifica tecnica mediante tecnologie avanzate, spazzole rotanti e sistemi di aspirazione HEPA per il ripristino igienico-funzionale.",
    icon: Sun,
    timing: "OPERATIVO"
  }, {
    number: "04",
    title: "VERIFICA FINALE",
    description: "Test di efficienza post-intervento e rilascio del report fotografico dettagliato con certificazione dell'avvenuta riqualificazione.",
    icon: Droplets,
    timing: "CONCLUSIVO"
  }];
  return <section data-section-id="4796" id="visibleversusinternalrisk" className="bg-slate-50 text-slate-900 py-20 md:py-28 lg:py-32 px-6 md:px-10 lg:px-16 overflow-hidden">
      <div className="max-w-[1400px] mx-auto">
        
        <div ref={headerRef} className={`grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 mb-20 md:mb-24 transition-all duration-1000 ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          
          <div className="lg:col-span-5">
            <p className="font-default text-xs uppercase tracking-widest text-secondary mb-6">
              [ 04 / METODO OPERATIVO ]
            </p>
            <h2 className="font-default font-bold text-secondary text-6xl sm:text-7xl md:text-8xl lg:text-[8rem] leading-[0.9] tracking-tighter uppercase">
              METODO
              <br />
              OPERATIVO
              <br />
              AERA SANUM
              <span className="text-primary">.</span>
            </h2>
          </div>

          
          <div className="lg:col-span-7 lg:pl-8 flex flex-col justify-between gap-12">
            <div className="max-w-[540px]">
              <p className="font-default text-lg md:text-xl leading-relaxed text-slate-800 mb-6">
                Aera Sanum applica protocolli rigorosi per la riqualificazione igienico-funzionale di impianti HVAC e sistemi di aspirazione. Ogni fase è documentata per garantire la massima efficienza e sicurezza degli ambienti.
              </p>
              <p className="font-default text-base leading-relaxed text-slate-500">
                Il nostro approccio tecnico assicura il ripristino delle prestazioni originali degli impianti, riducendo i consumi e garantendo la salubrità dell'aria.
              </p>
            </div>

            
            <div className="border-t border-slate-200 pt-8">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-8">
                {metrics.map((metric, i) => <div key={i} data-index={i} className="flex flex-col">
                    <span className="font-default font-bold text-3xl md:text-4xl lg:text-5xl leading-none text-primary tracking-tight">
                      {metric.value}
                      <span className="text-primary">.</span>
                    </span>
                    <span className="font-default text-[10px] md:text-xs uppercase tracking-widest text-slate-500 mt-3">
                      {metric.label}
                    </span>
                  </div>)}
              </div>
            </div>
          </div>
        </div>

        
        <div className="flex items-center gap-6 mb-12 md:mb-16">
          <span className="font-default text-xs uppercase tracking-widest text-secondary whitespace-nowrap">
            [ SEQUENZA INTERVENTO — 4 FASI ]
          </span>
          <Separator className="flex-1 bg-slate-200" />
        </div>

        
        <div ref={cardsRef} className={`transition-all duration-1000 delay-200 ${cardsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="flex gap-6 md:gap-8 overflow-x-auto pb-12 pr-6 -mx-6 px-6 md:mx-0 md:px-0 snap-x snap-mandatory">
            {protocolSteps.map((step, i) => {
            const Icon = step.icon;
            return <div key={i} data-index={i} className="flex items-stretch shrink-0 snap-start">
                  <div className="relative">
                    
                    <div className="absolute inset-0 translate-x-3 translate-y-3 bg-primary" aria-hidden="true" />
                    <Card className="relative w-[280px] md:w-[320px] lg:w-[340px] h-full bg-white backdrop-blur-sm border border-slate-200 py-0 gap-0 flex flex-col rounded-lg shadow-xl">
                      <CardContent className="p-7 md:p-8 flex flex-col h-full gap-6">
                        
                        <div className="flex items-start justify-between">
                          <span className="font-default font-bold text-6xl md:text-7xl leading-none tracking-tighter" style={{
                        WebkitTextStroke: "1.5px #00153D",
                        color: "transparent"
                      }}>
                            {step.number}
                          </span>
                          <div className="border border-slate-300 p-2.5">
                            <Icon className="size-5 text-secondary" strokeWidth={1.5} />
                          </div>
                        </div>

                        
                        <h3 className="font-default font-bold text-lg md:text-xl uppercase tracking-tight text-primary leading-tight">
                          {step.title}
                        </h3>

                        
                        <p className="font-default text-sm md:text-[15px] leading-relaxed text-slate-600 flex-1">
                          {step.description}
                        </p>

                        
                        <div className="flex items-center justify-between border-t border-slate-200 pt-4 mt-auto">
                          <span className="font-default text-[10px] uppercase tracking-widest text-slate-500">
                            FASE
                          </span>
                          <span className="font-default text-xs font-medium text-secondary">
                            {step.timing}
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  
                  {i < protocolSteps.length - 1 && <div className="hidden xl:flex items-center px-4 lg:px-6">
                      <div className="flex flex-col items-center gap-2">
                        <div className="w-12 h-px bg-slate-300" />
                        <span className="font-default text-[10px] uppercase tracking-widest text-slate-500 whitespace-nowrap">
                          SUCCESSIVO
                        </span>
                      </div>
                    </div>}
                </div>;
          })}
          </div>
        </div>
      </div>
    </section>;
}